/**
 * One box in the site's box list (#804): the JSS machine record and its
 * Kubernetes node on a single row, with SideroLink / Talos API / kubelet
 * health reported separately, versions, hardware identity, and every action
 * for the box — guided recovery and the Kubernetes node actions — in one menu.
 * "Allow re-flash" (JSS#883 security review) sits with recovery: a box that
 * proved its own key rejoins from the USB image only once an Admin allows it.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, MoreVertical, RotateCcw, AlertTriangle, ShieldCheck, Lock } from 'lucide-react';
import { Role, canActAs } from '@jeket-cloud/types/auth';
import { cn } from '@/lib/utils';
import { RoleGate } from '@/components/auth/RoleGate';
import { useUserRoles } from '@/contexts/AuthContext';
import { MachineStatusBadge } from '@/components/deployments/MachineStatusBadge';
import { describeMachineStatus } from '@/lib/machineStatus';
import {
  boxStatusReason,
  boxVersions,
  formatAge,
  hardwareIdentity,
  type Box,
  type Indicator,
  type IndicatorState,
} from '@/lib/boxes';
import {
  formatLocalDateTime,
  recoveryEligibility,
  reflashApproval,
  suggestedRecovery,
  type RecoveryAction,
} from '@/lib/boxRecovery';
import type { ClusterNode, Machine } from '@/types';
import { NodeDetails, NodeMenuItems, hasNodeMenuItems } from './NodeParts';
import { isNodePoolTaintKey, nodeAccepts, nodePool } from '@/lib/nodePools';

const DOT: Record<IndicatorState, string> = {
  ok: 'bg-status-healthy',
  warn: 'bg-status-warning',
  fail: 'bg-status-error',
  unknown: 'bg-silver-muted/60',
  na: 'border border-silver-muted/40',
};
const TEXT: Record<IndicatorState, string> = {
  ok: 'text-silver-muted',
  warn: 'text-status-warning',
  fail: 'text-status-error',
  unknown: 'text-silver-muted italic',
  na: 'text-silver-muted/60',
};
const STATE_WORD: Record<IndicatorState, string> = {
  ok: 'healthy',
  warn: 'warning',
  fail: 'failing',
  unknown: 'unknown',
  na: 'not applicable',
};

export function IndicatorLine({ indicator }: { indicator: Indicator }) {
  return (
    <div
      className="flex items-center gap-2 text-[11px] leading-5"
      title={indicator.detail}
      data-testid={`indicator-${indicator.key}`}
      data-state={indicator.state}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', DOT[indicator.state])} aria-hidden="true" />
      <span className="w-16 shrink-0 text-silver-muted">{indicator.name}</span>
      <span className={cn('truncate', TEXT[indicator.state])}>
        <span className="sr-only">{STATE_WORD[indicator.state]}: </span>
        {indicator.short}
      </span>
    </div>
  );
}

function RoleBadge({ role }: { role: Box['role'] }) {
  if (!role) return null;
  const isCP = role === 'controlplane';
  return (
    <span
      className={cn(
        'px-1.5 py-0.5 rounded text-[10px] font-medium',
        isCP ? 'bg-gold/10 text-gold border border-gold/20' : 'bg-surface-elevated text-silver-bright',
      )}
      title={isCP ? 'Control plane: runs the cluster itself, and workloads' : 'Worker'}
    >
      {isCP ? 'Control plane' : 'Worker'}
    </span>
  );
}

/** The node's pool (JSS#1104), from the labels JSS writes; a lock when its pool is dedicated and tainted. */
export function PoolBadge({ node }: { node: ClusterNode | null | undefined }) {
  const pool = nodePool(node?.labels);
  if (!node || !pool) return null;
  const tainted = (node.taints ?? []).some((t) => isNodePoolTaintKey(t.key));
  const accepts = nodeAccepts(node.labels);
  return (
    <span
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-surface-elevated text-silver-bright border border-surface-elevated"
      title={`Node pool ${pool}: accepts ${accepts.join(', ') || 'no class'}${tainted ? '; dedicated (tainted)' : ''}`}
      data-testid="pool-badge"
    >
      {tainted && <Lock size={9} aria-label="dedicated" />}
      {pool}
    </span>
  );
}

export interface BoxRowProps {
  box: Box;
  indicators: Indicator[];
  onRecover: (box: Box, action: RecoveryAction) => void;
  onRemove: (machine: Machine) => void;
  /** Open the "Allow re-flash" confirmation (JSS#883). */
  onAllowReflash: (machine: Machine) => void;
  onNodeAction: (node: ClusterNode, action: string) => void;
  onNodeDiagnostics?: (node: ClusterNode) => void;
  onNodeEditTaints?: (node: ClusterNode) => void;
}

const MENU_ITEM = 'w-full text-left px-3 py-1.5 text-xs text-silver-bright hover:bg-gold/10 transition-colors';

export default function BoxRow({
  box,
  indicators,
  onRecover,
  onRemove,
  onAllowReflash,
  onNodeAction,
  onNodeDiagnostics,
  onNodeEditTaints,
}: BoxRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const roles = useUserRoles();

  const { machine: m, node } = box;
  const identity = hardwareIdentity(box);
  const versions = boxVersions(box);
  const reason = boxStatusReason(box, indicators);
  const elig = recoveryEligibility(m);
  const suggested = suggestedRecovery(m, indicators);
  const now = Date.now();
  const reflash = reflashApproval(m, now);

  const canReset = elig.reset && canActAs(roles, Role.Admin);
  const canReinstall = elig.reinstall && canActAs(roles, Role.Engineer);
  const canAllowReflash = reflash.kind === 'offer' && canActAs(roles, Role.Admin);
  const canRemove = !!m && canActAs(roles, Role.Admin);
  // A node's own items are Admin-only but Diagnostics (JSS#970): below that,
  // an empty menu would be all that's left of them.
  const hasNodeMenu = !!node && hasNodeMenuItems(roles, !!onNodeDiagnostics);
  const hasMenu = canReset || canReinstall || canAllowReflash || canRemove || hasNodeMenu;

  const identityLine = [identity.product, identity.gpus.join(', ') || null, identity.product ? null : identity.cpu]
    .filter(Boolean)
    .join(' · ');
  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <tr
        className={cn(
          'border-b border-surface-elevated hover:bg-surface-elevated/30 cursor-pointer transition-colors align-top',
          expanded && 'bg-surface-elevated/20',
        )}
        onClick={() => setExpanded(!expanded)}
        data-testid={`box-row-${box.key}`}
      >
        {/* Box */}
        <td className="px-4 py-3">
          <div className="flex items-start gap-2">
            {expanded ? (
              <ChevronDown size={14} className="text-gold mt-1 shrink-0" />
            ) : (
              <ChevronRight size={14} className="text-silver-muted mt-1 shrink-0" />
            )}
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                {/* Read-only: hostnames are rendered by JSS (`<cluster>-<role>-<n>`),
                    and the customer rename route is gone (JSS#907). */}
                <span className="font-mono text-sm text-silver-bright">{box.hostname}</span>
                <RoleBadge role={box.role} />
                <PoolBadge node={node} />
                {m?.maintenanceDetected && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/20 text-amber-400 flex items-center gap-1">
                    <AlertTriangle size={10} />
                    maintenance mode
                  </span>
                )}
              </div>
              <p className="text-[11px] text-silver-muted truncate max-w-[18rem]" title={identityLine || undefined}>
                {identityLine || identity.arch || 'Hardware not read yet'}
              </p>
            </div>
          </div>
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <div className="space-y-1 max-w-xs">
            {m ? (
              <MachineStatusBadge status={m.status} />
            ) : (
              <span
                className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-silver-muted/20 text-silver-muted"
                title="A Kubernetes node with no Jeket machine record"
              >
                No Jeket record
              </span>
            )}
            {reason && (
              <p className="text-[11px] text-silver-muted line-clamp-2" title={reason} data-testid="box-status-reason">
                {reason}
              </p>
            )}
            {reflash.kind === 'offer' && reflash.refusedAt && (
              <p className="text-[11px] text-status-warning" data-testid="box-reflash-refused">
                A box tried to rejoin as this one from the USB image {formatAge(now - new Date(reflash.refusedAt).getTime())} ago
                and was refused.
              </p>
            )}
            {reflash.kind === 'impersonated' && (
              <p className="text-[11px] text-status-error" data-testid="box-reflash-impersonated">
                Someone presented this box&apos;s identity with the site&apos;s USB image{' '}
                {formatAge(now - new Date(reflash.refusedAt).getTime())} ago and was refused. This box still connects with
                its own key, so it was not re-flashed: replace the USB image.
              </p>
            )}
          </div>
        </td>

        {/* Health */}
        <td className="px-4 py-3 min-w-[14rem]">
          {indicators.map((i) => (
            <IndicatorLine key={i.key} indicator={i} />
          ))}
        </td>

        {/* Versions */}
        <td className="px-4 py-3 text-[11px] font-mono whitespace-nowrap">
          <div><span className="text-silver-muted">Talos </span><span className="text-silver-bright">{versions.talos ? `v${versions.talos}` : '—'}</span></div>
          <div><span className="text-silver-muted">K8s </span><span className="text-silver-bright">{versions.kubernetes ? `v${versions.kubernetes}` : '—'}</span></div>
        </td>

        {/* Actions */}
        <td className="px-4 py-3 relative" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-2">
            {reflash.kind === 'open' && (
              <span className="text-[11px] text-silver-muted whitespace-nowrap" data-testid="reflash-allowed-until">
                Re-flash allowed until {formatLocalDateTime(reflash.until)}
              </span>
            )}
            {m && reflash.kind === 'offer' && reflash.refusedAt && (
              <RoleGate role={Role.Admin}>
                <button
                  type="button"
                  onClick={() => onAllowReflash(m)}
                  className="text-xs px-2.5 py-1 bg-gold/10 text-gold rounded hover:bg-gold/20 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  <ShieldCheck size={12} />
                  Allow re-flash (24 h)
                </button>
              </RoleGate>
            )}
            {m && suggested === 'reset' && (
              <RoleGate role={Role.Admin}>
                <button
                  type="button"
                  onClick={() => onRecover(box, 'reset')}
                  className="text-xs px-2.5 py-1 bg-status-error/10 text-status-error rounded hover:bg-status-error/20 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  <RotateCcw size={12} />
                  Reset to maintenance
                </button>
              </RoleGate>
            )}
            {m && suggested === 'reinstall' && (
              <RoleGate role={Role.Engineer}>
                <button
                  type="button"
                  onClick={() => onRecover(box, 'reinstall')}
                  className="text-xs px-2.5 py-1 bg-amber-500/10 text-amber-400 rounded hover:bg-amber-500/20 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  <RotateCcw size={12} />
                  Reinstall
                </button>
              </RoleGate>
            )}
            {hasMenu && (
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-1 rounded hover:bg-surface-elevated transition-colors"
                aria-label={`Actions for ${box.hostname}`}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <MoreVertical size={14} className="text-silver-muted" />
              </button>
            )}
          </div>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={closeMenu} />
              <div role="menu" className="absolute right-4 top-10 z-50 bg-surface-elevated border border-surface-elevated rounded-lg shadow-xl py-1 min-w-[180px]">
                {m && elig.reset && (
                  <RoleGate role={Role.Admin}>
                    <button type="button" onClick={() => { onRecover(box, 'reset'); closeMenu(); }} className={MENU_ITEM}>
                      Reset to maintenance…
                    </button>
                  </RoleGate>
                )}
                {m && elig.reinstall && (
                  <RoleGate role={Role.Engineer}>
                    <button type="button" onClick={() => { onRecover(box, 'reinstall'); closeMenu(); }} className={MENU_ITEM}>
                      Reinstall…
                    </button>
                  </RoleGate>
                )}
                {m && reflash.kind === 'offer' && (
                  <RoleGate role={Role.Admin}>
                    <button type="button" onClick={() => { onAllowReflash(m); closeMenu(); }} className={MENU_ITEM}>
                      Allow re-flash (24 h)…
                    </button>
                  </RoleGate>
                )}
                {node && hasNodeMenu && (
                  <div className={cn((canReset || canReinstall || canAllowReflash) && 'border-t border-surface-elevated mt-1 pt-1')}>
                    <NodeMenuItems
                      node={node}
                      onAction={onNodeAction}
                      onDiagnostics={onNodeDiagnostics}
                      onEditTaints={onNodeEditTaints}
                      onClose={closeMenu}
                    />
                  </div>
                )}
                {m && (
                  <RoleGate role={Role.Admin}>
                    <div className="border-t border-surface-elevated mt-1 pt-1">
                      <button
                        type="button"
                        onClick={() => { onRemove(m); closeMenu(); }}
                        className="w-full text-left px-3 py-1.5 text-xs text-status-error hover:bg-status-error/10 transition-colors"
                      >
                        Remove box…
                      </button>
                    </div>
                  </RoleGate>
                )}
              </div>
            </>
          )}
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-surface-elevated">
          <td colSpan={5} className="bg-surface-deep/50 px-6 py-4">
            <BoxDetails box={box} indicators={indicators} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 text-xs">
      <span className="text-silver-muted shrink-0">{label}</span>
      <span className={cn('text-silver-bright text-right break-all', mono && 'font-mono')}>{value}</span>
    </div>
  );
}

function BoxDetails({ box, indicators }: { box: Box; indicators: Indicator[] }) {
  const { machine: m, node } = box;
  const identity = hardwareIdentity(box);
  const status = m ? describeMachineStatus(m.status) : null;
  const warnings = m?.prereqWarnings ?? [];

  return (
    <div className="space-y-4">
      {/* Health, in full */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Health</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {indicators.map((i) => (
            <div key={i.key} className="bg-surface border border-surface-elevated rounded-lg p-3 space-y-1" data-testid={`indicator-detail-${i.key}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className={cn('w-2 h-2 rounded-full', DOT[i.state])} aria-hidden="true" />
                <span className="text-silver-bright font-medium">{i.name}</span>
                <span className={cn('ml-auto', TEXT[i.state])}>{i.short}</span>
              </div>
              <p className="text-[11px] text-silver-muted leading-snug">{i.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {m?.errorMessage && (
        <div className="flex items-start gap-2 bg-status-error/10 border border-status-error/30 rounded px-3 py-2 text-xs text-status-error">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span className="break-words">{m.errorMessage}</span>
        </div>
      )}

      {/* Pre-flight warnings (#23) and unsupported-hardware reasons (#802). */}
      {warnings.length > 0 && (
        <div className="space-y-1">
          {warnings.map((w, idx) => (
            <div
              key={idx}
              className={cn(
                'flex items-start gap-2 rounded px-3 py-2 text-xs border',
                w.severity === 'block'
                  ? 'bg-status-error/10 border-status-error/30 text-status-error'
                  : 'bg-status-warning/10 border-status-warning/20 text-status-warning',
              )}
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Box</h4>
          <div className="space-y-1">
            {status && <DetailRow label="Status" value={`${status.label} — ${status.description}`} />}
            {status && <DetailRow label="Status code" value={status.code} mono />}
            <DetailRow label="Product" value={identity.product} />
            <DetailRow label="CPU" value={identity.cpu} mono />
            <DetailRow label="Memory" value={identity.memory} mono />
            <DetailRow label="GPUs" value={identity.gpus.join(', ') || null} mono />
            <DetailRow label="Architecture" value={identity.arch} mono />
            <DetailRow label="Talos stage" value={m?.talosStage} mono />
            <DetailRow
              label="Config pushed"
              value={m?.configPushedAt ? new Date(m.configPushedAt).toLocaleString() : null}
            />
            {!m && <p className="text-xs text-silver-muted">No Jeket machine record for this node.</p>}
          </div>
        </div>
        <div>
          {node ? (
            <NodeDetails node={node} />
          ) : (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Kubernetes</h4>
              <p className="text-xs text-silver-muted">Not in the cluster{m && describeMachineStatus(m.status).tone === 'progress' ? ' yet' : ''}.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
